import React, { useEffect, useState, useRef } from 'react';
import locales from '../../../content.json';
import { useSelector, useDispatch } from 'react-redux';
import { showPaymentModal, updatePurchaseFlow, hidePaymentModal } from '../../../reduxStore/navigation/actions';
import { RootState } from '../../../store';
import { WebCatalogProduct } from '../../../utils/services/EveryDoggyAPITypes';
import EveryDoggyAPIServices from '../../../utils/services/EveryDoggyAPIServices';
import { getPricesConfig } from './components/offer/getPricesConfig';
import { PAYMENT_TARIFF } from '../../../types';
import { setPaymentProduct, setPaymentFreeBonus } from '../../../reduxStore/data/actions';
import Guarantee from '../../../components/common/guarantee';
import { FaqV2 } from '../../../components/everydoggyV5/faqV2';
import { SupportBlock } from '../../../components/everydoggyV2/supportBlock/supportBlock';
import { Footer } from '../../../components/everydoggyV2/footer/footer';
import { getParameterFromURL } from '../../../utils/scripts';
import { getCurrencySymbol } from '../../../utils/scripts/currency';
import PaymentModal from './components/paymentModalV2';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '../../../utils/analytics';
import s from './index.module.scss';

import img1 from './img/img1.png';
import img2 from './img/img2.png';

type Prices = {
  firstOption?: WebCatalogProduct;
};

export const ChatUpsale = () => {
  const dispatch = useDispatch();
  const lang = window.userLang || 'en';
  const locale = locales[lang] || locales.en || {};
  const paymentModal = useSelector((state: RootState) => state.data.paymentModal);
  const product = paymentModal.productName?.name;
  const price = paymentModal?.product?.details?.amount || paymentModal?.product?.details?.trial_amount;
  const [prices, setPrices] = useState<Prices>({});
  const { firstPriceConfig } = getPricesConfig(PAYMENT_TARIFF.DEFAULT).defaultDesign;
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const labelRef = useRef(null);
  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const subscrEU = getParameterFromURL('subscrEU');
  const subscrUK = getParameterFromURL('subscrUK');

  useEffect(() => {
    dispatch(setPaymentFreeBonus(false));
  }, []);

  // iOS: lock <body> scroll while this full-screen overlay is open. The paywall
  // behind it lives in a scrollable body; when the payment modal opens (input
  // focus / keyboard) the body shifts and the position:fixed overlay no longer
  // covers it, so the underlying paywall bleeds through behind the Safari bars.
  useEffect(() => {
    const { body } = document;
    const scrollY = window.scrollY;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, []);

  let subscription;
  if (subscrEU) {
    subscription = 'everydoggy_2WEEK_29_99_EUR_INTRO_30days_9_99_TRAINER_CHAT_EVERYDOGGY_EVERYDOGGY';
  } else if (subscrUK) {
    subscription = 'everydoggy_2WEEK_29_99_GBP_INTRO_30days_9_99_TRAINER_CHAT_EVERYDOGGY_EVERYDOGGY';
  } else {
    subscription = 'everydoggy_2WEEK_29_99_USD_INTRO_30days_9_99_TRAINER_CHAT_EVERYDOGGY_EVERYDOGGY';
  }

  const currency = getCurrencySymbol();

  const contentIdsChat = [subscription];

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const firstOption = await EveryDoggyAPIServices.getWebCatalogProduct(firstPriceConfig.id.toString());
      setPrices({ firstOption });
    }
    fetchData();
  }, []);

  useEffect(() => {
    const defaultProductId = getPricesConfig(PAYMENT_TARIFF.DEFAULT).defaultDesign.firstPriceConfig.id;
    const productName = subscription;
    const oldPrice = '85.68';

    const product = {
      product: defaultProductId.toString(),
      oldPrice,
      name: productName,
    };

    dispatch(setPaymentProduct(JSON.stringify(product)));
  }, [prices]);

  useEffect(() => {
    addEventsShowChatUpsele(contentIdsChat);
  }, []);

  const showSuccessScreen = () => {
    dispatch(updatePurchaseFlow({}));
  };

  function addEventsShowChatUpsele(contentIdsChat: string[]) {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showChatUpsale(contentIdsChat), EVENT_TRACKERS.GTM);
  }

  function closeChatUpsale() {
    showSuccessScreen();
  }

  function doOnClick() {
    dispatch(showPaymentModal({ product: product, currency: currency, price: Number(price) }));
  }

  const handleClosePaymentModal = () => {
    dispatch(hidePaymentModal());

    requestAnimationFrame(() => {
      chatWrapperRef.current?.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    });
  };

  return (
    <div className={s.dogTrainerChat} ref={chatWrapperRef}>
      <section className={s.mainWrapper}>
        <div className={s.imageWrapper}>
          <img src={img1} alt="chat img" />
        </div>

        <div className={s.labelWrapper}>
          <div className={s.label}>
            <p>{locale.chatUpsaleV2_1}</p>
            <span></span>
          </div>
        </div>

        <div className={s.titleWrapper}>
          <p className={s.title}>{locale.chatUpsaleV2_2}</p>
        </div>

        <div className={s.subtitleWrapper}>
          <p className={s.subtitle}>{locale.chatUpsaleV2_3}</p>
        </div>

        <div className={s.cardWrapper}>
          <div className={`${s.card} ${s.card1}`}>
            <div className={s.cardLabelWrapper}>
              <div className={s.cardLabel}>
                <div className={s.cardLabelImage} />
                <p className={s.cardLabelText}>{locale.chatUpsaleV2_4}</p>
              </div>
            </div>
            <div className={s.cardHeader}>
              <p className={s.cardText}>{locale.chatUpsaleV2_5}</p>
              <div className={s.cardLines}>
                <div className={`${s.cardLine} ${s.cardLine1}`} />
                <div className={`${s.cardLine} ${s.cardLine2}`} />
                <div className={`${s.cardLine} ${s.cardLine3}`} />
              </div>
              <div className={s.cardItems}>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg1}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_6}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg2}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_7}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg3}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_8}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg4}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_9}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={s.cardWrapper}>
          <div className={`${s.card} ${s.card2}`}>
            <div className={s.cardLabelWrapper}>
              <div className={s.cardLabel}>
                <div className={s.cardLabelImage} />
                <p className={s.cardLabelText}>{locale.chatUpsaleV2_10}</p>
              </div>
            </div>
            <div className={s.cardHeader}>
              <p className={s.cardText}>{locale.chatUpsaleV2_5}</p>
              <div className={s.cardLines}>
                <div className={`${s.cardLine} ${s.cardLine1}`} />
                <div className={`${s.cardLine} ${s.cardLine2}`} />
                <div className={`${s.cardLine} ${s.cardLine3}`} />
              </div>
              <div className={s.cardItems}>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg1}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_11}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg2}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_12}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg3}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_13}</p>
                </div>
                <div className={s.cardItem}>
                  <div className={`${s.cardItemImg} ${s.cardItemImg4}`} />
                  <p className={s.cardItemText}>{locale.chatUpsaleV2_14}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={s.imageWrapper2}>
          <img src={img2} alt="chat img" />
        </div>

        <div className={s.titleWrapper2}>
          <p className={s.title}>{locale.chatUpsaleV2_15}</p>
        </div>

        <div className={s.itemsWrapper}>
          <div className={s.item}>
            <div className={`${s.itemImage} ${s.itemImage1}`} />
            <p className={s.itemText}>{locale.chatUpsaleV2_16}</p>
          </div>

          <div className={s.item}>
            <div className={`${s.itemImage} ${s.itemImage2}`} />
            <p className={s.itemText}>{locale.chatUpsaleV2_17}</p>
          </div>

          <div className={s.item}>
            <div className={`${s.itemImage} ${s.itemImage3}`} />
            <p className={s.itemText}>{locale.chatUpsaleV2_18}</p>
          </div>

          <div className={s.item}>
            <div className={`${s.itemImage} ${s.itemImage4}`} />
            <p className={s.itemText}>{locale.chatUpsaleV2_19}</p>
          </div>
        </div>
      </section>

      <section className={s.achieveWrapper}>
        <div className={s.achieveContent}>
          <div className={s.titleWrapper3}>
            <p className={s.title}>{locale.chatUpsaleV2_20}</p>
          </div>

          <div className={s.labelWrapper2}>
            <div className={s.label}>
              <p>75% OFF</p>
            </div>
          </div>

          <div className={s.labelWrapper3}>
            <div className={s.label}>
              <div className={s.labelBlock1Wrapper}>
                <div className={s.labelBlock1}>
                  <span className={s.labelBlock1Image1}></span>
                  <p className={s.labelBlock1Text}>{locale.chatUpsaleV2_21}</p>
                  <span className={s.labelBlock1Image2}></span>
                </div>
              </div>

              <div className={s.labelBlock2Wrapper} ref={containerRef}>
                <div className={s.labelBlock2}>
                  <div className={s.fixed}>
                    <span>3</span>
                    <span className={s.hidden}>7</span>
                  </div>
                  <div className={`${s.labelBlock2Cell} ${visible ? s.isVisible : ''}`}>
                    <p>
                      <span className={s.hidden}>3</span>
                      <span>7</span>
                    </p>
                    <p>
                      <span className={s.hidden}>3</span>
                      <span>6</span>
                    </p>
                    <p>
                      <span className={s.hidden}>3</span>
                      <span>5</span>
                    </p>
                    <p>
                      <span className={s.hidden}>3</span>
                      <span>4</span>
                    </p>
                    <p>
                      <span className={s.hidden}>3</span>
                      <span>3</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className={s.labelBlock3Wrapper}>
                <div className={s.labelBlock3}>
                  <p>{locale.chatUpsaleV2_22}</p>
                  <div className={s.labelBlock3Loader}>
                    <span></span>
                  </div>
                </div>
              </div>

              <div className={s.labelBlock4Wrapper}>
                <div className={s.labelBlock4PriceBlock}>
                  <p className={s.labelBlock4PriceText}>{locale.chatUpsaleV2_23}</p>
                  <p className={s.labelBlock4Price}>{currency}39.99</p>
                </div>

                <div className={`${s.labelBlock4PriceBlock} ${s.labelBlock4PriceBlock2}`}>
                  <p className={s.labelBlock4PriceText}>{locale.chatUpsaleV2_24}</p>
                  <p className={s.labelBlock4Price}>-{currency}29.99</p>
                </div>

                <div className={s.labelBlock4Divider} />

                <div className={`${s.labelBlock4PriceBlock} ${s.labelBlock4PriceBlock3}`}>
                  <p className={s.labelBlock4PriceText}>{locale.chatUpsaleV2_25}:</p>
                  <p className={s.labelBlock4Price}>{currency}9.99</p>
                </div>
              </div>

              <div className={s.labelBlock5Wrapper}></div>
            </div>

            <div className={s.labelBlockButtons}>
              <button className={s.labelBlockButton1} data-test="open-payment-modal" onClick={doOnClick}>
                <p>{locale.chatUpsaleV2_26}</p>
                <span></span>
                <svg className={s.arrow} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M1.25 10H18.125M18.125 10L9.375 2.5M18.125 10L9.375 17.5" stroke="white" strokeWidth="1.875" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button className={s.labelBlockButton2} onClick={closeChatUpsale}>
                <p>{locale.chatUpsaleV2_27}</p>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className={s.policyWrapper}>
        <div className={s.guaranteeWrapper}>
          <Guarantee discount={true} />
        </div>
        <div className={s.faqWrapper}>
          <FaqV2 theme1={true} />
        </div>
        <div className={s.supportWrapper}>
          <SupportBlock />
        </div>
        <div className={s.footerWrapper}>
          <Footer tariff={'withSecondStyle'} />
        </div>
      </section>

      <PaymentModal
        label={labelRef.current}
        onClose={handleClosePaymentModal}
      />
    </div>
  );
};
